import {useCallback, useMemo, useReducer} from 'react';
import {RgbaColor} from 'react-colorful';
import {COLORS} from 'constants/index';
import {clamp, debounce} from 'lodash';
import {sortkey} from 'utils/sort';

import {ImageType} from './useImages/query';

type Config = {
	color: RgbaColor,
	scale: number,
	title: string,
	order: number,
	style?: React.CSSProperties,
}

type State = Record<ImageType, Config> & {
	counter: number
};

const defaults = (): State => ({
	'BMD': {
		color: {r: 255, g: 255, b: 255, a: 1},
		scale: 1.5,
		title: 'Bone',
		order: 3,
		style: {
			backgroundColor: COLORS.NEWDARK55,
		}
	},

	'Fat Mass': {
		color: {r: 0, g: 2, b: 255, a: 1},
		scale: 1.5,
		title: 'Fat Tissue',
		order: 1,
	},

	'PST': {
		color: {r: 255, g: 81, b: 0, a: 0.73},
		scale: 1.25,
		title: 'Lean Tissue',
		order: 2,
	},

	counter: 0,
});

type Action =
  | { type: 'SET', key: ImageType, color: Config['color'] }
;

const reducer = (state: State, action: Action): State => {
	switch (action.type) {
	case 'SET':
		return {
			...state,
			[action.key]: {
				...state[action.key],
				color: action.color,
			},
			counter: state.counter + 1,
		};
	}
};

export type SetColor = (color: RgbaColor) => void
export type FillOptions = {
	scale?: number
}
export type FillStyle = (v: number, opts?: FillOptions) => string
export type ImageConfig = Config & {
	setColor: SetColor
	fillStyle: FillStyle
}

export type Colors = {
	config: (type: ImageType) => ImageConfig | null
	types: ImageType[]
	counter: number
}

export const useColors = (): Colors => {
	const [state, dispatch] = useReducer(reducer, defaults());
	const debounceMillis = 200;

	// Iterate our state to calculate list of ordered ImageTypes
	const types = useMemo(() => (
		Object
			.keys(state)
			.filter((k) => k !== 'counter')
			.map((k) => ({
				k,
				v: state[k as ImageType],
			}))
			.sort(sortkey(({v}) => v.order))
			.map(({k}) => k as ImageType)
	), []);

	// Compute a config lookup functio
	const config = useCallback((type: ImageType) => {
		if (!(type in state)) {
			return null;
		}

		const {color, scale, ...rest} = state[type];
		return {
			color, scale, ...rest,

			setColor: debounce(
				(color: RgbaColor) => dispatch({type: 'SET', key: type, color}),
				debounceMillis,
			),

			fillStyle: (v: number, opts?: FillOptions) => {
				const scl = opts?.scale ? opts.scale : scale;
				return (
					`rgba(
						${color.r},
						${color.g},
						${color.b},
						${clamp(color.a * v * scl, 0, 1)}
					)`
				);
			},
		};
	}, [state.counter]);

	return {
		config,
		types,
		counter: state.counter,
	};
};
